import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { RefreshCw, Search, MapPin, Clock, AlertTriangle, Route, Hourglass, Radio } from 'lucide-react';

/**
 * Europcar · Araç Arama Talebi
 *
 * Backend: GET /api/europcar-ga4/search-demand?days=7|14|30
 *
 * Kaynak bizim GTM etiketimizin gönderdiği `arac_arama` olayı
 * (/pixel/europcar/gtm-arama-takibi.html). GTM v23 (7 Eki 2026) ile olay doğru
 * tetikleyiciye bağlandı, alanlar aynı gün GA4'e özel tanım olarak eklendi.
 * GA4 tanımları geçmişe dönük doldurmaz → veri 7 Eki'den başlar.
 *
 * 🔑 "Arama" = sonucsuz alanı 0/1 olan olay. Alanları boş olanlar (eski çöp
 *    tetiklemeler + tanım öncesi aramalar) ayrı "alanları boş" kovasında,
 *    ortalamalara girmez. Hesaplar backend'de; sayfa yalnız gösterir.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const RANGES = [{ d: 7, l: '7 gün' }, { d: 14, l: '14 gün' }, { d: 30, l: '30 gün' }];
const AYLAR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const dLabel = (d) => (d && d.length === 8 ? `${Number(d.slice(6, 8))} ${AYLAR[Number(d.slice(4, 6)) - 1]}` : '—');
const isoLabel = (s) => (s ? dLabel(s.replace(/-/g, '')) : '—');
const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
const nf1 = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 1 }));
const pct = (x, d = 1) => (x == null ? '—' : `%${(x * 100).toLocaleString('tr-TR', { maximumFractionDigits: d })}`);
const tl = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('tr-TR')} ₺`);

const C = { sonuclu: '#047857', sonucsuz: '#e11d48', bos: '#cbd5e1', rez: '#0f172a' };

/* ---------------------------------------------------------------- parçacıklar */

function Section({ icon: Icon, eyebrow, title, note, children, className = '' }) {
  return (
    <section className={`mt-8 ${className}`}>
      <div className="flex items-start gap-2.5">
        {Icon ? <Icon size={17} className="mt-[3px] shrink-0 text-emerald-700" /> : null}
        <div>
          <h2 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
          <p className="mt-1 text-[19px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
          {note ? <p className="mt-1 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">{note}</p> : null}
        </div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Kpi({ label, value, sub, tone }) {
  const t = tone === 'rose' ? 'text-rose-700' : tone === 'slate' ? 'text-slate-900' : 'text-emerald-800';
  return (
    <div className="border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-[24px] font-bold leading-none tracking-tight tabular-nums ${t}`}>{value}</div>
      {sub ? <div className="mt-1.5 text-[12px] leading-snug text-slate-500">{sub}</div> : null}
    </div>
  );
}

function Th({ children, right, onClick, active }) {
  return (
    <th
      onClick={onClick}
      className={`sticky top-0 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider ${active ? 'text-slate-900' : 'text-slate-500'} ${right ? 'text-right' : 'text-left'} ${onClick ? 'cursor-pointer select-none hover:text-slate-900' : ''}`}
    >
      {children}{active ? ' ↓' : ''}
    </th>
  );
}
function Td({ children, right, className = '' }) {
  return (
    <td className={`border-b border-slate-100 px-3 py-2 text-[13px] text-slate-700 ${right ? 'text-right tabular-nums' : ''} ${className}`}>
      {children}
    </td>
  );
}

function Bos({ children }) {
  return (
    <div className="flex h-[220px] items-center justify-center border border-dashed border-slate-300 bg-slate-50 px-6 text-center text-[13px] text-slate-500">
      {children}
    </div>
  );
}

const tooltipStyle = { fontSize: 12, borderRadius: 0, border: '1px solid #e2e8f0' };

/* --------------------------------------------------------------- ofis tablosu */

const OFIS_KOLON = [
  { k: 'arama', l: 'Arama', f: nf },
  { k: 'kullanici', l: 'Kullanıcı', f: nf },
  { k: 'sonucsuz', l: 'Sonuçsuz', f: nf },
  { k: 'sonucsuzOran', l: 'Sonuçsuz %', f: (x) => pct(x, 0) },
  { k: 'ortAracSayisi', l: 'Ort. araç', f: nf1 },
  { k: 'ortEnDusukFiyat', l: 'En düşük fiyat', f: tl },
  { k: 'ortFiyat', l: 'Ort. fiyat', f: tl },
  { k: 'ortOncelikGun', l: 'Kaç gün önce', f: nf1 },
  { k: 'ortKiralamaGun', l: 'Kiralama günü', f: nf1 }
];

function OfisTablosu({ rows }) {
  const [sort, setSort] = useState('arama');
  const [hepsi, setHepsi] = useState(false);
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b[sort] ?? -1) - (a[sort] ?? -1)),
    [rows, sort]
  );
  const max = Math.max(1, ...rows.map((r) => r.arama));
  const gor = hepsi ? sorted : sorted.slice(0, 20);
  if (!rows.length) return <Bos>Ofis kırılımı, alanları dolu ilk aramalar GA4'e düşünce burada görünecek.</Bos>;
  return (
    <div className="border border-slate-200 bg-white">
      <div className="max-h-[640px] overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Alış ofisi</Th>
              {OFIS_KOLON.map((c) => (
                <Th key={c.k} right onClick={() => setSort(c.k)} active={sort === c.k}>{c.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gor.map((r) => (
              <tr key={r.ofis} className="hover:bg-slate-50">
                <Td className="min-w-[240px]">
                  <div className="font-medium text-slate-900">{r.ofis}</div>
                  <div className="mt-1 h-1.5 bg-slate-100">
                    <div className="flex h-1.5" style={{ width: `${(r.arama / max) * 100}%` }}>
                      <div style={{ flex: r.arama - r.sonucsuz, background: C.sonuclu }} />
                      <div style={{ flex: r.sonucsuz, background: C.sonucsuz }} />
                    </div>
                  </div>
                </Td>
                {OFIS_KOLON.map((c) => (
                  <Td key={c.k} right className={c.k === 'sonucsuzOran' && r.sonucsuzOran >= 0.2 ? 'font-semibold text-rose-700' : ''}>
                    {c.f(r[c.k])}
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 20 ? (
        <button onClick={() => setHepsi(!hepsi)} className="w-full border-t border-slate-200 py-2 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">
          {hepsi ? 'İlk 20 ofisi göster' : `Tüm ${rows.length} ofisi göster`}
        </button>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------------- sayfa */

export default function EuropcarAramaTalebi() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (refresh) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/europcar-ga4/search-demand?days=${days}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Arama verisi yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(false); }, [load]);

  if (!brandId) {
    return <div className="mx-auto max-w-[900px] px-4 py-10 text-[14px] text-slate-600">Önce bir marka seçin.</div>;
  }

  const d = data;
  const o = d?.ozet || {};
  const bekliyor = d && !o.arama;
  const bugun = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const gunluk = (d?.gunluk || []).map((g) => ({ ...g, etiket: dLabel(g.date) + (g.date === bugun ? ' (bugün)' : '') }));
  const kacan = (d?.ofisler || []).filter((r) => r.sonucsuz > 0)
    .sort((a, b) => b.sonucsuz - a.sonucsuz).slice(0, 12)
    .map((r) => ({ ...r, kisa: r.ofis.length > 34 ? `${r.ofis.slice(0, 33)}…` : r.ofis }));
  const saatVar = (d?.saat || []).some((s) => s.sonuclu + s.sonucsuz > 0);

  return (
    <div lang="tr" className="mx-auto max-w-[1360px] px-4 py-6">

      {/* --------------------------------------------------------- başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">Europcar · Araç Arama Talebi</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Kim, hangi ofiste, ne zaman araç arıyor?
          </p>
          <p className="mt-1.5 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">
            Sitede yapılan her araç araması: ofis, tarih, kaç araç çıktığı ve fiyat. Kaynak canlı GA4 (
            <span className="font-semibold text-slate-700">arac_arama</span> olayı).
            {d?.meta ? ` ${isoLabel(d.meta.range.start)} → ${isoLabel(d.meta.range.end)}, bugün kısmi.` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {d?.realtime && !d.realtime.error ? (
            <div className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[12px] text-emerald-900">
              <Radio size={13} className="text-emerald-600" />
              <span><b className="tabular-nums">{nf(d.realtime.arama)}</b> arama · <b className="tabular-nums">{nf(d.realtime.rezervasyon)}</b> rezervasyon</span>
              <span className="text-emerald-700">son 30 dk</span>
            </div>
          ) : null}
          <div className="flex border border-slate-300">
            {RANGES.map((r) => (
              <button
                key={r.d} onClick={() => setDays(r.d)}
                className={`px-3 py-1.5 text-[12px] font-semibold ${days === r.d ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{r.l}</button>
            ))}
          </div>
          <button
            onClick={() => load(true)} disabled={loading}
            className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />Yenile
          </button>
        </div>
      </div>

      {!d && loading ? <div className="mt-8 text-[14px] text-slate-500">GA4'e soruluyor…</div> : null}

      {d ? (
        <>
          {bekliyor ? (
            <div className="mt-5 flex items-start gap-2.5 border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
              <Hourglass size={16} className="mt-0.5 shrink-0" />
              <div>
                <b>Alanlar dolmayı bekliyor.</b> Arama olayı geliyor (bu dönemde {nf(o.hamArama)} olay, son 30 dakikada {nf(d.realtime?.arama)}),
                ama ofis, fiyat ve sonuç alanları GA4'e 7 Ekim'de tanımlandı. GA4 yeni tanımları 24–48 saat içinde
                doldurur ve geçmişe dönük çalışmaz. Kutular veri geldikçe kendiliğinden dolacak.
              </div>
            </div>
          ) : null}

          {/* ------------------------------------------------------- KPI */}
          <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
            <Kpi label="Arama" value={nf(o.arama)} sub={o.bos ? `+${nf(o.bos)} alanları boş olay` : `${nf(o.ofisSayisi)} ofiste`} tone="slate" />
            <Kpi label="Sonuçsuz arama" value={pct(o.sonucsuzOran)} sub={`${nf(o.sonucsuz)} aramada hiç araç çıkmadı`} tone="rose" />
            <Kpi label="Arama başına rezervasyon" value={pct(o.aramaBasinaRez)} sub={`${nf(o.rezervasyon)} rezervasyon`} />
            <Kpi label="Ort. en düşük fiyat" value={tl(o.ortEnDusukFiyat)} sub={`raf ortalaması ${tl(o.ortFiyat)}`} />
            <Kpi label="Kaç gün önceden" value={o.ortOncelikGun == null ? '—' : `${nf1(o.ortOncelikGun)} gün`} sub="aramadan alışa kalan" />
            <Kpi label="Kiralama süresi" value={o.ortKiralamaGun == null ? '—' : `${nf1(o.ortKiralamaGun)} gün`} sub={`ort. ${nf1(o.ortAracSayisi)} araç listeleniyor`} />
            <Kpi label="Tek yön" value={pct(o.tekYonOran)} sub="farklı ofise iade" />
          </div>

          {/* ---------------------------------------------------- günlük */}
          <Section
            icon={Search} eyebrow="Talep"
            title="Günlük arama ve rezervasyon"
            note="Yeşil sonuçlu, kırmızı hiç araç çıkmayan arama. Gri sütun alanları boş gelen olaylar (tanımlar yapılmadan önceki aramalar). Çizgi aynı günün rezervasyonu."
          >
            {gunluk.length ? (
              <div className="border border-slate-200 bg-white p-3">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={gunluk} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="etiket" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis yAxisId="a" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={44} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={34} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => nf(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                    <Bar yAxisId="a" dataKey="sonuclu" name="Sonuçlu arama" stackId="s" fill={C.sonuclu} maxBarSize={44} />
                    <Bar yAxisId="a" dataKey="sonucsuz" name="Sonuçsuz arama" stackId="s" fill={C.sonucsuz} maxBarSize={44} />
                    <Bar yAxisId="a" dataKey="bos" name="Alanları boş" stackId="s" fill={C.bos} maxBarSize={44} />
                    <Line yAxisId="r" dataKey="rezervasyon" name="Rezervasyon" stroke={C.rez} strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            ) : <Bos>Bu dönemde arama olayı yok.</Bos>}
          </Section>

          {/* ------------------------------------- kaçan talep + saat */}
          <div className="grid gap-x-6 lg:grid-cols-2">
            <Section
              icon={AlertTriangle} eyebrow="Kaçan talep"
              title="Hiç araç çıkmayan aramalar, ofis ofis"
              note="Müşteri aradı, raf boş geldi. Kapasite yüzünden kaybedilen talebin doğrudan ölçüsü."
            >
              {kacan.length ? (
                <div className="border border-slate-200 bg-white p-3">
                  <ResponsiveContainer width="100%" height={Math.max(200, kacan.length * 30)}>
                    <BarChart data={kacan} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="kisa" width={220} tick={{ fontSize: 11, fill: '#334155' }} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={tooltipStyle}
                        formatter={(v, n, p) => [`${nf(v)} (aramaların ${pct(p.payload.sonucsuzOran, 0)})`, 'Sonuçsuz']}
                        labelFormatter={(_, p) => (p && p[0] ? p[0].payload.ofis : '')} />
                      <Bar dataKey="sonucsuz" fill={C.sonucsuz} maxBarSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <Bos>{bekliyor ? 'Veri bekleniyor.' : 'Bu dönemde sonuçsuz arama yok.'}</Bos>}
            </Section>

            <Section
              icon={Clock} eyebrow="Alış saati"
              title="Araç hangi saatte teslim alınmak isteniyor?"
              note="Aramadaki alış saati (arama yapılan saat değil). Kırmızı kısım o saatte sonuç çıkmayan aramalar."
            >
              {saatVar ? (
                <div className="border border-slate-200 bg-white p-3">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={d.saat} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="saat" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={(h) => `${h}`} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={40} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} labelFormatter={(h) => `${String(h).padStart(2, '0')}:00`} formatter={(v) => nf(v)} />
                      <Bar dataKey="sonuclu" name="Sonuçlu" stackId="s" fill={C.sonuclu} />
                      <Bar dataKey="sonucsuz" name="Sonuçsuz" stackId="s" fill={C.sonucsuz} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : <Bos>Veri bekleniyor.</Bos>}
            </Section>
          </div>

          {/* ------------------------------------------------------- ofis */}
          <Section
            icon={MapPin} eyebrow="Ofisler"
            title="Alış ofisine göre talep, fiyat ve zamanlama"
            note="Sütun başlığına tıklayıp sıralayın. Fiyatlar yalnız sonuçlu aramalardan, rafın toplam (tüm gün) fiyatı. Sonuçsuz oranı %20 ve üstü kırmızı."
          >
            <OfisTablosu rows={d.ofisler || []} />
          </Section>

          {/* ------------------------------------------------------- rota */}
          <Section
            icon={Route} eyebrow="Tek yön"
            title="Farklı ofise iade edilmek istenen rotalar"
            note="Alış ve iade ofisi farklı olan aramalar, en çok arananlar."
          >
            {(d.rotalar || []).length ? (
              <div className="overflow-x-auto border border-slate-200 bg-white">
                <table className="w-full border-collapse">
                  <thead><tr><Th>Alış</Th><Th>İade</Th><Th right>Arama</Th><Th right>Sonuçsuz</Th></tr></thead>
                  <tbody>
                    {d.rotalar.map((r) => (
                      <tr key={`${r.alis}→${r.donus}`} className="hover:bg-slate-50">
                        <Td>{r.alis || '—'}</Td><Td>{r.donus || '—'}</Td>
                        <Td right>{nf(r.arama)}</Td>
                        <Td right className={r.sonucsuz ? 'text-rose-700' : ''}>{nf(r.sonucsuz)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Bos>{bekliyor ? 'Veri bekleniyor.' : 'Bu dönemde tek yön arama yok.'}</Bos>}
          </Section>

          <p className="mt-8 border-t border-slate-200 pt-3 text-[12px] leading-relaxed text-slate-500">
            Kaynak: GA4 property {d.meta.propertyId}, <b>arac_arama</b> olayı (Glowytics GTM etiketi, araç listesi sayfasındaki
            arama kutusundan okunur). Veri {isoLabel(d.meta.veriBaslangic)} itibarıyla; öncesindeki olaylar parametresiz tetiklendiği
            için hesaba katılmaz. Rezervasyon = aynı günün purchase olayı; arama ile birebir eşleştirilmez.
            {d.meta.cached ? ' Önbellekten (10 dk).' : ''}
          </p>
        </>
      ) : null}
    </div>
  );
}
